import type { BreadcrumbList } from "@decocms/apps-commerce/types";

const PROD_URL_HTTPS = "https://www.danielatombini.com.br";
const ACCOUNT_NAME = "danielatombini";

// A conta VTEX devolve `link`/`url` de produtos e categorias apontando pro
// host de um sales channel/ambiente técnico, não pro domínio público da loja.
// Fixo/determinístico nos dois lados (servidor e cliente), então não causa
// mismatch de hidratação — ao contrário de uma correção baseada em
// `location`/`request`.
const VTEX_LEAKED_HOSTS = new Set([
  "proxy.danielatombini.com.br",
  "danielatombini.vtexcommercestable.com.br",
  new URL(PROD_URL_HTTPS).host,
]);

// Devolve caminho relativo (não absoluto) pros hosts acima: um link absoluto
// pro domínio de produção manda o usuário pro site real de verdade sempre
// que o preview/dev roda em outro host (deco.host, workers.dev, etc.) — foi
// exatamente isso que aconteceu ao clicar num seletor de tamanho/cor durante
// teste em ambiente de preview e cair no v1 em produção. Caminho relativo
// resolve certo em qualquer host, e continua determinístico nos dois lados
// (servidor e cliente) — não reintroduz o risco de mismatch de hidratação
// que a versão anterior evitava.
function sanitizeStoreUrl<T extends string | undefined>(url: T): T {
  if (!url) return url;
  try {
    const parsed = new URL(url, PROD_URL_HTTPS);
    if (!VTEX_LEAKED_HOSTS.has(parsed.host)) return url;
    return `${parsed.pathname}${parsed.search}${parsed.hash}` as T;
  } catch {
    return url;
  }
}

// Normaliza uma URL vinda da VTEX (proxy/sales channel, com query strings
// como `?skuId=`) pro caminho canônico no domínio público, sem query — é o
// que o Google exige em `url` de Product/ListItem no rich result.
function canonicalFor(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    return `${PROD_URL_HTTPS}${new URL(url).pathname}`;
  } catch {
    return undefined;
  }
}

// BreadcrumbJsonLd (vendorizado, @decocms/blocks/hooks)
// publica item.item tal como vem do loader da VTEX, sem normalização —
// diferente do canonical/og:url/ItemList da mesma página, que já usam
// canonicalFor. Resultado: o BreadcrumbList da PDP apontava pro domínio
// interno da VTEX (proxy.danielatombini.com.br); o de categoria/busca vinha
// como path relativo (sem domínio nenhum) — dois formatos de entrada
// diferentes. `canonicalFor` exige uma URL já absoluta (`new URL(url)` sem
// base, lança em path relativo); por isso resolve-se aqui contra
// `PROD_URL_HTTPS` antes de chamar `canonicalFor`, cobrindo os dois casos
// sem precisar mudar `canonicalFor` em si.
function sanitizeBreadcrumb(breadcrumb: BreadcrumbList): BreadcrumbList {
  return {
    ...breadcrumb,
    itemListElement: breadcrumb.itemListElement.map((item) => {
      let absolute: string | undefined;
      try {
        absolute = new URL(item.item, PROD_URL_HTTPS).href;
      } catch {
        absolute = undefined;
      }
      return { ...item, item: canonicalFor(absolute) ?? item.item };
    }),
  };
}

export {
  ACCOUNT_NAME,
  canonicalFor,
  PROD_URL_HTTPS,
  sanitizeBreadcrumb,
  sanitizeStoreUrl,
};
